/**
 * Execute a compose plan commit against manufacturing recipe APIs.
 * Creates theoretical stubs, then products with component lines.
 * Supports partial commits via productNodeIds.
 */
import {
  createManufacturingRecipe,
  setManufacturingRecipeLines,
  updateManufacturingRecipe,
} from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import { buildCommitPlanOrder, type ComposeCommitResult } from "./commitPlan";
import type { ComposePlan } from "./types";

export type { ComposeCommitResult };

async function ensureRecipeGroup(
  recipeId: string,
  groupId: string | null,
  existing: MfgRecipeSummary | undefined,
): Promise<void> {
  if (!groupId) return;
  if (existing && (existing.recipe_group_id ?? null) === groupId) return;
  await updateManufacturingRecipe({
    recipe_id: recipeId,
    recipe_group_id: groupId,
  });
}

export async function executeComposeCommit(
  plan: ComposePlan,
  existingRecipes: readonly MfgRecipeSummary[],
  opts?: { productNodeIds?: readonly string[] },
): Promise<ComposeCommitResult> {
  const order = buildCommitPlanOrder(plan, opts);
  const recipeIdByNodeId: Record<string, string> = { ...order.matchedByNodeId };
  const byCode = new Map(
    existingRecipes.map((r) => [r.code.trim().toUpperCase(), r] as const),
  );
  const byId = new Map(existingRecipes.map((r) => [r.id, r] as const));

  const createdStubs: string[] = [];
  const createdProducts: string[] = [];
  const linkedProducts: string[] = [];
  const failed: Array<{ code: string; error: string }> = [];
  const committedProductNodeIds: string[] = [];
  const groupId =
    typeof plan.archetype.recipe_group_id === "string" && plan.archetype.recipe_group_id.trim()
      ? plan.archetype.recipe_group_id.trim()
      : null;

  if (order.products.length === 0) {
    return {
      createdStubs: [],
      createdProducts: [],
      linkedProducts: [],
      failed: [{ code: "", error: "No ready products selected to commit." }],
      recipeIdByNodeId,
      committedProductNodeIds: [],
    };
  }

  // Ensure already-matched components land in the compose group too.
  for (const [nodeId, recipeId] of Object.entries(order.matchedByNodeId)) {
    void nodeId;
    await ensureRecipeGroup(recipeId, groupId, byId.get(recipeId));
  }

  for (const stub of order.stubs) {
    const existing = byCode.get(stub.code.trim().toUpperCase());
    if (existing) {
      recipeIdByNodeId[stub.nodeId] = existing.id;
      await ensureRecipeGroup(existing.id, groupId, existing);
      continue;
    }
    const res = await createManufacturingRecipe({
      code: stub.code,
      name: stub.name,
      kind: stub.kind,
      tags: stub.tags,
      recipe_group_id: groupId,
      needs_review: true,
    });
    if (!res.ok) {
      failed.push({ code: stub.code, error: res.error });
      continue;
    }
    recipeIdByNodeId[stub.nodeId] = res.data.recipe.id;
    byCode.set(stub.code.trim().toUpperCase(), res.data.recipe);
    byId.set(res.data.recipe.id, res.data.recipe);
    createdStubs.push(res.data.recipe.id);
  }

  for (const product of order.products) {
    let productId = recipeIdByNodeId[product.nodeId];
    const existing = byCode.get(product.code.trim().toUpperCase());

    if (!productId && existing) {
      productId = existing.id;
      recipeIdByNodeId[product.nodeId] = productId;
      linkedProducts.push(productId);
      await ensureRecipeGroup(productId, groupId, existing);
    } else if (!productId) {
      const res = await createManufacturingRecipe({
        code: product.code,
        name: product.name,
        kind: "product",
        tags: product.tags,
        recipe_group_id: groupId,
        needs_review: true,
      });
      if (!res.ok) {
        failed.push({ code: product.code, error: res.error });
        continue;
      }
      productId = res.data.recipe.id;
      recipeIdByNodeId[product.nodeId] = productId;
      byCode.set(product.code.trim().toUpperCase(), res.data.recipe);
      byId.set(productId, res.data.recipe);
      createdProducts.push(productId);
    } else {
      await ensureRecipeGroup(productId, groupId, byId.get(productId));
    }

    const resolvedLines: Array<{
      component_recipe_id: string;
      slot_label: string;
    }> = [];
    for (const line of product.componentLines) {
      const rid = recipeIdByNodeId[line.nodeId];
      if (rid) {
        resolvedLines.push({
          component_recipe_id: rid,
          slot_label: line.slot_label,
        });
      }
    }
    if (resolvedLines.length === 0) {
      failed.push({
        code: product.code,
        error: "No resolved component lines for this product.",
      });
      continue;
    }

    const linesRes = await setManufacturingRecipeLines({
      recipe_id: productId,
      lines: resolvedLines.map((line, i) => ({
        component_recipe_id: line.component_recipe_id,
        quantity: 1,
        sort_order: i,
        role: "structural" as const,
        slot_label: line.slot_label,
      })),
    });
    if (!linesRes.ok) {
      failed.push({ code: product.code, error: linesRes.error });
      continue;
    }
    committedProductNodeIds.push(product.nodeId);
  }

  return {
    createdStubs,
    createdProducts,
    linkedProducts,
    failed,
    recipeIdByNodeId,
    committedProductNodeIds,
  };
}
